import includes from "lodash/includes";

/**
 * Represents a region defined for use in an analysis.
 * Region sets are immutable and are *not* a resource as they can be used by anyone, anywhere.
 */
interface RegionSet {
  /** The primary identifier of the region set. */
  regionSetId: number | null;

  /** The granularity determines the interpretation of the regionCode. */
  granularity: RegionGranularity;

  /**
   * The region code is an identifier that is interpreted differently based off the granularity.
   * If MultiRegion, then null.
   * If State, then StateAbbreviation.
   * If CBSA, then CBSACode.
   * If County, then CountySSA.
   * If CountyZip, then CountySSA.
   * If Zip, then ZipCode.
   */
  regionCode: string | null;

  /*
   * If MultiRegion, State, CBSA, County, or Zip, then null.
   * If CountyZip, then ZipCode.
   */
  subRegionCode: string | null;

  /**
   * The partialRegionSets are specified if a subset of the RegionCode is represented.
   * MultiRegion can parent any Granularity.
   * State can parent a County or Zip.
   * CBSA can parent a County.
   * County can parent a CountyZip.
   * CountyZip and Zip cannot be a parent.
   */
  partialRegionSets: RegionSet[] | null;
}

/** The granularity defines the interpretation of a particular region. */
enum RegionGranularity {
  Unknown = "unknown",
  MultiRegion = "multiRegion",
  State = "state",
  CBSA = "cbsa",
  County = "county",
  CountyZip = "countyZip",
  Zip = "zip",
}

/**
 * Determines the allowed parents for a particular granularity.
 * @param targetGranularity The granularity being inspected.
 */
function allowedParents(targetGranularity: RegionGranularity): RegionGranularity[] {
  const allowedGranularities: RegionGranularity[] = [RegionGranularity.MultiRegion];
  switch (targetGranularity) {
    case RegionGranularity.County:
      allowedGranularities.push(RegionGranularity.State, RegionGranularity.CBSA);
      break;
    case RegionGranularity.Zip:
      allowedGranularities.push(RegionGranularity.State);
      break;
    case RegionGranularity.CountyZip:
      allowedGranularities.push(RegionGranularity.County);
      break;
  }

  return allowedGranularities;
}

/**
 * Determines whether a target granularity can be parented under a given region set.
 * @param regionSet The region set.
 * @param targetGranularity The target granularity.
 */
function canBeParented(regionSet: RegionSet, targetGranularity: RegionGranularity): boolean {
  const parents = allowedParents(targetGranularity);
  return includes(parents, regionSet.granularity);
}

export { allowedParents, canBeParented, RegionGranularity, RegionSet as default };
